/**
 * Copyable (COPY-ANYWHERE, Seth 2026-06-25) — right-click (or hover the copy
 * icon) to copy a value anywhere in Pegasus: an address, phone, email, or the
 * whole name + address block as a mailing label, e.g.
 *
 *   Seth Simonson
 *   20315 Erickson Path
 *   Farmington, MN 55024
 *
 * One primitive, reused system-wide ([[feedback_declutter_best_in_class]]).
 * The hover copy-icon makes it discoverable (right-click alone is invisible).
 *
 * Three shapes:
 *   default  — inline value (phone/email link): icon sits inline on hover.
 *   block    — a multi-line block (name + address): icon floats top-right.
 *   bare     — wraps already-stacked block lines without touching layout
 *              (display:contents) and shows NO icon — right-click only. Use on
 *              dense rows that already have their own hover affordances.
 *
 * COPY-01 (Seth 2026-09-01: "click on models, serial numbers, etc will copy to
 * clipboard… prevalent on the mobile side… press and hold"): CODES are a
 * different animal from names/phones — a code is never a link, so the click
 * itself is free. <CopyCode> renders model / serial / part # / PO # / call # /
 * invoice # / customer # as ONE chip everywhere: click copies (desktop),
 * tap copies AND press-and-hold copies (phone), right-click still offers the
 * "Copy …" menu, and it stops the click so a chip inside a row that opens or
 * toggles something never triggers the row. Stripe Dashboard's click-to-copy
 * IDs + Apple's hold-to-copy, in one component.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Copy, Check, ListChecks } from "lucide-react";
import { cn } from "@/lib/cn";

/** Put text on the clipboard. Resolves true when it landed. Falls back to the
 *  legacy execCommand path for the cases navigator.clipboard refuses — the
 *  phone hitting the dev server over plain http, older webviews. */
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Haptic tick where the platform allows it (Android web). iOS web apps can't
 *  buzz until the native shell — the visual "Copied" state carries it there. */
function haptic() {
  try {
    if (navigator.vibrate) navigator.vibrate(12);
  } catch {
    /* fine */
  }
}

/** The shared right-click menu (one item: "Copy …"). Portaled so it escapes
 *  overflow clipping and modals. */
function CopyMenu({
  menu,
  label,
  onCopy,
  onClose,
}: {
  menu: { x: number; y: number } | null;
  label: string;
  onCopy: () => void;
  onClose: () => void;
}) {
  if (!menu) return null;
  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[200]"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        style={{
          position: "fixed",
          top: Math.min(menu.y, window.innerHeight - 48),
          left: Math.min(menu.x, window.innerWidth - 180),
          zIndex: 201,
        }}
        className="w-44 overflow-hidden rounded-lg border border-hairline bg-white py-1 shadow-e2"
      >
        <button
          type="button"
          onClick={onCopy}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <Copy className="h-3.5 w-3.5 shrink-0 text-slate-400" /> {label}
        </button>
      </div>
    </>,
    document.body,
  );
}

export function Copyable({
  text,
  label = "Copy",
  className,
  iconClassName,
  block,
  bare,
  children,
}: {
  /** The exact text placed on the clipboard. */
  text: string;
  /** Menu + tooltip wording, e.g. "Copy address" / "Copy phone". */
  label?: string;
  className?: string;
  /** Override the copy-icon colors (e.g. white on a dark image overlay). */
  iconClassName?: string;
  /** A multi-line block (name + address): the copy icon floats top-right. */
  block?: boolean;
  /** Layout-neutral, right-click-only (no icon) — for dense already-stacked rows. */
  bare?: boolean;
  children: ReactNode;
}) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const doCopy = () => {
    void copyText(text);
    setCopied(true);
    setMenu(null);
    window.setTimeout(() => setCopied(false), 1300);
  };

  const onContextMenu = (e: React.MouseEvent) => {
    if (!text) return;
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY });
  };

  const menuPortal = <CopyMenu menu={menu} label={label} onCopy={doCopy} onClose={() => setMenu(null)} />;

  // bare — no box, no icon, right-click only. Children keep their own layout.
  if (bare) {
    return (
      <span style={{ display: "contents" }} onContextMenu={onContextMenu}>
        {children}
        {menuPortal}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "group/cp relative",
        block ? "block" : "inline-flex items-center gap-1",
        className,
      )}
      onContextMenu={onContextMenu}
    >
      {children}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          doCopy();
        }}
        title={label}
        aria-label={label}
        className={cn(
          "shrink-0 rounded p-0.5 text-slate-300 opacity-0 transition-all hover:bg-slate-100 hover:text-slate-600 focus:opacity-100 group-hover/cp:opacity-100",
          block && "absolute right-1 top-1",
          iconClassName,
        )}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-emerald-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
      {menuPortal}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   COPY-01 — the code chip.
   ──────────────────────────────────────────────────────────────────────────── */

const HOLD_MS = 450;

type CodeSize = "sm" | "md" | "lg";

const CHIP_SIZE: Record<CodeSize, string> = {
  sm: "px-1.5 py-[1px] text-[12.5px]",
  md: "px-2 py-0.5 text-[13.5px]",
  lg: "px-2.5 py-1 text-[15px]",
};
const TEXT_SIZE: Record<CodeSize, string> = {
  sm: "text-[12.5px]",
  md: "text-[13.5px]",
  lg: "text-[15px]",
};
const GLYPH_SIZE: Record<CodeSize, string> = {
  sm: "h-3 w-3",
  md: "h-3.5 w-3.5",
  lg: "h-4 w-4",
};

/**
 * A model number, serial, part #, PO #, call #, invoice #, customer # — any
 * code a person would otherwise retype. Click / tap / hold to copy.
 *
 *   <CopyCode value={e.serial} label="Copy serial" />
 *   <CopyCode value={po.po_number} label="Copy PO number" variant="text">PO 20001</CopyCode>
 *
 * `variant="chip"` (default) is the boxed mono chip; `variant="text"` keeps the
 * surrounding typography and only adds the cursor + hover glyph — for values
 * that are already the row's headline. `onDark` recolors for navy headers.
 */
export function CopyCode({
  value,
  label,
  variant = "chip",
  size = "sm",
  onDark,
  mono = true,
  className,
  children,
  title,
}: {
  /** The exact text placed on the clipboard. Renders as the label unless children are given. */
  value: string | null | undefined;
  /** Menu + tooltip wording, e.g. "Copy serial". Defaults to "Copy". */
  label?: string;
  variant?: "chip" | "text";
  size?: CodeSize;
  /** Chip sits on a dark surface (the mobile job header). */
  onDark?: boolean;
  /** Monospace (default) — set false when the code is a row's headline and
   *  should keep the surrounding Inter weight. */
  mono?: boolean;
  className?: string;
  children?: ReactNode;
  /** Hover tooltip override (default: the label). */
  title?: string;
}) {
  const text = (value ?? "").toString().trim();
  const wording = label ?? "Copy";
  const [copied, setCopied] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const holdTimer = useRef<number | null>(null);
  const held = useRef(false);
  const touch = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const resetTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (holdTimer.current) window.clearTimeout(holdTimer.current);
      if (resetTimer.current) window.clearTimeout(resetTimer.current);
    },
    [],
  );

  if (!text) return children ? <>{children}</> : null;

  const doCopy = () => {
    void copyText(text);
    haptic();
    setCopied(true);
    setMenu(null);
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopied(false), 1300);
  };
  const clearHold = () => {
    if (holdTimer.current) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const base =
    variant === "chip"
      ? cn(
          "inline-flex items-center gap-1 rounded-md font-medium tabular-nums tracking-[-0.01em] ring-1 ring-inset transition-colors",
          mono && "font-mono",
          CHIP_SIZE[size],
          copied
            ? onDark
              ? "bg-emerald-400/25 text-emerald-100 ring-emerald-300/40"
              : "bg-emerald-50 text-emerald-700 ring-emerald-200"
            : onDark
              ? "bg-white/10 text-white ring-white/15 hover:bg-white/20"
              : "bg-slate-100/90 text-slate-700 ring-black/[0.05] hover:bg-slate-200/80 hover:text-slate-900",
        )
      : cn(
          "-mx-0.5 inline-flex items-center gap-1 rounded px-0.5 tabular-nums transition-colors",
          mono && "font-mono",
          TEXT_SIZE[size],
          copied
            ? onDark
              ? "text-emerald-200"
              : "bg-emerald-50 text-emerald-700"
            : onDark
              ? "hover:bg-white/10"
              : "hover:bg-slate-100",
        );

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        title={title ?? (copied ? "Copied" : `${wording} — click, or hold on a phone`)}
        aria-label={copied ? "Copied" : wording}
        data-copied={copied || undefined}
        className={cn(
          "group/cc cursor-copy select-none [-webkit-touch-callout:none] [touch-action:manipulation] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          base,
          className,
        )}
        onPointerDown={(e) => {
          touch.current = e.pointerType !== "mouse";
          held.current = false;
          if (!touch.current) return;
          start.current = { x: e.clientX, y: e.clientY };
          clearHold();
          holdTimer.current = window.setTimeout(() => {
            held.current = true;
            doCopy();
          }, HOLD_MS);
        }}
        onPointerUp={clearHold}
        onPointerCancel={clearHold}
        onPointerLeave={clearHold}
        onPointerMove={(e) => {
          // A finger that travels is scrolling, not holding.
          const s = start.current;
          if (touch.current && s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 8) clearHold();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (held.current) {
            held.current = false;
            return;
          }
          doCopy();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Android fires contextmenu on a long-press — the hold already copied.
          if (touch.current) return;
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            doCopy();
          }
        }}
      >
        {/* Never truncate a code — a clipped serial is worse than a wrapped one. */}
        <span className="whitespace-nowrap">{children ?? text}</span>
        {copied ? (
          <Check className={cn(GLYPH_SIZE[size], "shrink-0", onDark ? "text-emerald-200" : "text-emerald-600")} strokeWidth={2.5} />
        ) : (
          <Copy
            className={cn(
              GLYPH_SIZE[size],
              "shrink-0 opacity-0 transition-opacity group-hover/cc:opacity-100 group-focus-visible/cc:opacity-100 [@media(hover:none)]:opacity-55",
              onDark ? "text-white/70" : "text-slate-400",
            )}
          />
        )}
      </span>
      <CopyMenu menu={menu} label={wording} onCopy={doCopy} onClose={() => setMenu(null)} />
    </>
  );
}

/**
 * "Copy list" — every model / part number on a pull sheet or PO as one
 * newline-separated block, ready to paste into a vendor portal or an email
 * (COPY-01 warehouse/PO upgrade). Hidden when the list is empty.
 */
export function CopyListButton({
  items,
  label = "Copy list",
  noun = "items",
  className,
  size = "sm",
}: {
  items: Array<string | null | undefined>;
  label?: string;
  /** Plural noun for the confirmation, e.g. "part numbers". */
  noun?: string;
  className?: string;
  size?: "sm" | "md";
}) {
  const [done, setDone] = useState(false);
  const list = items.map((s) => (s ?? "").toString().trim()).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void copyText(list.join("\n"));
        haptic();
        setDone(true);
        window.setTimeout(() => setDone(false), 1600);
      }}
      title={`${label} — ${list.length} ${noun}, one per line`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-md border font-semibold transition-colors [touch-action:manipulation]",
        size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-[13px]",
        done
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-hairline bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900",
        className,
      )}
    >
      {done ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : <ListChecks className="h-3.5 w-3.5 text-slate-400" />}
      {done ? `Copied ${list.length} ${noun}` : label}
    </button>
  );
}

/** Build a clean mailing-label block from the parts (skips blanks). */
export function mailingLabel(parts: {
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string {
  const cityLine = [parts.city, [parts.state, parts.zip].filter(Boolean).join(" ")]
    .filter((s) => s && s.trim())
    .join(", ");
  return [parts.name, parts.address, cityLine]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join("\n");
}
